"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import posthog from "posthog-js";

interface BridgeCtaTrackingProperties {
	bridge_surface: "result_card" | "result_link" | "spoke_hero" | "spoke_footer";
	locale: string;
	channel: string;
	peptide_slug: string;
	product_slug: string;
	variant_id: string | null;
	match_type: "exact" | "larger_vial" | "smaller_vial" | "none";
	cta_action: "view_product" | "direct_add";
}

/** Observe the actual action element; mounted cards are a separate historical denominator. */
export function useBridgeCtaTracking({
	bridge_surface,
	locale,
	channel,
	peptide_slug,
	product_slug,
	variant_id,
	match_type,
	cta_action,
}: BridgeCtaTrackingProperties) {
	const [element, setElement] = useState<HTMLElement | null>(null);
	const viewed = useRef(new Set<string>());
	const properties = useMemo(
		() => ({
			bridge_version: "calculator_bridge_v2",
			bridge_surface,
			locale,
			channel,
			peptide_slug,
			product_slug,
			variant_id,
			match_type,
			cta_action,
		}),
		[bridge_surface, channel, cta_action, locale, match_type, peptide_slug, product_slug, variant_id],
	);
	const identity = JSON.stringify([locale, channel, peptide_slug, variant_id, bridge_surface, cta_action]);

	const capture = useCallback(
		(event: "calculator_bridge_cta_viewed" | "calculator_bridge_cta_clicked") => {
			if (!element) return;
			try {
				posthog.capture(event, {
					...properties,
					cta_top_px: Math.round(element.getBoundingClientRect().top + window.scrollY),
					viewport_height: document.documentElement.clientHeight,
					viewport_width: document.documentElement.clientWidth,
					scroll_y: Math.round(window.scrollY),
				});
			} catch {
				// Telemetry must never prevent navigation or a cart action.
			}
		},
		[element, properties],
	);

	useEffect(() => {
		if (!element || viewed.current.has(identity) || typeof IntersectionObserver === "undefined") return;
		let active = true;
		let timer: ReturnType<typeof setTimeout> | null = null;
		let meetsThreshold = false;
		const cancelTimer = () => {
			if (timer !== null) clearTimeout(timer);
			timer = null;
		};
		const updateTimer = () => {
			if (!active) return;
			if (!meetsThreshold || document.visibilityState !== "visible") {
				cancelTimer();
				return;
			}
			if (timer !== null || viewed.current.has(identity)) return;
			timer = setTimeout(() => {
				timer = null;
				if (!meetsThreshold || document.visibilityState !== "visible" || viewed.current.has(identity)) return;
				viewed.current.add(identity);
				capture("calculator_bridge_cta_viewed");
			}, 500);
		};
		const observer = new IntersectionObserver(
			([entry]) => {
				meetsThreshold = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.5);
				updateTimer();
			},
			{ threshold: [0, 0.5] },
		);
		observer.observe(element);
		document.addEventListener("visibilitychange", updateTimer);
		return () => {
			active = false;
			cancelTimer();
			observer.disconnect();
			document.removeEventListener("visibilitychange", updateTimer);
		};
	}, [capture, element, identity]);

	const trackClick = useCallback(() => capture("calculator_bridge_cta_clicked"), [capture]);
	return { setTrackedElement: setElement, trackClick };
}
